import { useState } from "react";

import { Alert, Heading, Span } from "@icg360/design-system";

import { SpinnerLoader } from "components/loader/loader";
import {
  PROPERTY_ROOF_CODE_MAP,
  PROPERTY_ROOF_TYPE_MAP,
  PROPERTY_ROOF_UPDATE_TYPE,
} from "consts";
import { type PropertySection } from "data/keystone-api-mapping";
import {
  useKeystone,
  usePolicyDetail,
  usePolicyId,
  useUser,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { usePolling } from "hooks/use-polling";
import { usePropertyUpdateRoof } from "pages/property-profile/property-update-roof/hooks";
import { logError, logException, trackEvent, useFlags } from "utils";

import {
  PropertyUpdateRoofContext,
  type PropertyUpdateRoofContextState,
} from ".";
import { FileThumbnails } from "../file-thumbnails";
import { PropertyUpdateLayout } from "../property-update-layout";
import styles from "./property-update-roof.module.scss";

const POLLING_TIMEOUT = 30000; // Thirty seconds
const POLLING_INTERVAL = 1000; // One second

export const RoofReview = () => {
  const keystone = useKeystone();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const { supportPhoneNumber } = useFlags();
  const user = useUser();
  const userEmail = user?.email ?? "";
  const insightPolicyId = usePolicyId();
  const { mutate: refreshPolicyData } = usePolicyDetail();
  const { formNavigate, state } =
    useMultiStepFormState<PropertyUpdateRoofContextState>(
      PropertyUpdateRoofContext
    );
  const { files, roofYear, roofMaterial } = state ?? {};

  const { startPolling } = usePolling({
    async action() {
      return await refreshPolicyData();
    },
    predicate(data) {
      const roofUpdateData =
        data?.userPropertyProfileData?.propertyUpdateData?.find(
          (detail) => detail?.detailName === "ROOF"
        );
      const hasYearUpdate =
        roofUpdateData?.items?.some(
          (update) => update?.enumerationValue === `${roofYear}`
        ) ?? false;
      const hasMaterialUpdate =
        roofUpdateData?.items?.some(
          (update) =>
            update?.enumerationValue ===
            `${PROPERTY_ROOF_TYPE_MAP[roofMaterial]}`
        ) ?? false;

      return hasYearUpdate && hasMaterialUpdate;
    },
    onComplete() {
      formNavigate("../success");
    },
    timeout: POLLING_TIMEOUT,
    interval: POLLING_INTERVAL,
  });

  const { prevRoofYear, prevRoofType } = usePropertyUpdateRoof();

  type PropertyErrorProps = {
    errorCode?: string | number;
    errorMessage?: string;
    errorType?: string;
  };

  const trackError = ({
    errorCode,
    errorMessage,
    errorType,
  }: PropertyErrorProps) => {
    const fileNames: string[] = [];
    const fileTypes: string[] = [];
    const fileSizes: number[] = [];
    files.forEach((file: File) => {
      fileNames.push(file.name);
      fileTypes.push(file.type);
      fileSizes.push(file.size);
    });
    trackEvent("PHE_Error", {
      id: "roof-update-submit-failed",
      numberOfFiles: files?.length,
      errorCode,
      errorMessage,
      errorType,
      fileNames,
      fileTypes,
      fileSizes,
    });
  };

  const onSubmit = async () => {
    setSubmitting(true);
    const request = {
      insightPolicyId,
      userEmail,
      propertySection: "ROOF" as PropertySection,
      propertyUpdateItems: [
        {
          termName: "Roof Year",
          enumerationValue: roofYear,
          currentDisplayValue: prevRoofYear ?? "N/A",
          updatedDisplayValue: roofYear,
        },
        {
          termName: "Roof Material",
          enumerationValue: PROPERTY_ROOF_TYPE_MAP[roofMaterial],
          currentDisplayValue: prevRoofType
            ? PROPERTY_ROOF_UPDATE_TYPE[PROPERTY_ROOF_CODE_MAP[prevRoofType]]
                ?.label ?? "N/A"
            : "N/A",
          updatedDisplayValue: PROPERTY_ROOF_UPDATE_TYPE[roofMaterial].label,
        },
      ],
    };

    try {
      const response = await keystone.updateProperty(request, files);

      if (!isError(response)) {
        startPolling();
      } else {
        setSubmitting(false);
        setError(true);
        logError(`Roof update submission error: ${response.message}`);
        trackError({
          errorMessage: response.message,
          errorType: "server_error",
        });
      }
    } catch (err) {
      setSubmitting(false);
      setError(true);
      logException(err);
      trackError({
        errorCode: err?.code,
        errorMessage: err?.message,
        errorType: "system_error",
      });
    }
  };

  if (submitting) {
    return (
      <div className={styles.loadingSpinner}>
        <SpinnerLoader firstMessage="Hang tight, we're sending your updates..." />
      </div>
    );
  }

  return (
    <PropertyUpdateLayout
      step={4}
      numSteps={4}
      heading="Update roof details"
      buttonProps={{
        primaryOnClick: onSubmit,
        primaryText: "Yes, submit",
      }}
    >
      <div className={styles.roofReview}>
        <Heading size="md" className={styles.reviewHeading}>
          Does this look right?
        </Heading>
        {error ? (
          <Alert
            appearance="danger"
            title="There was an error submitting your update."
            description={`Please try again. If the problem persists, contact support at ${supportPhoneNumber}.`}
          />
        ) : null}
        <Span>
          Year replaced: <Span bold>{state?.roofYear}</Span>
        </Span>
        <Span>
          Material:{" "}
          <Span bold>
            {PROPERTY_ROOF_UPDATE_TYPE[state?.roofMaterial].label}
          </Span>
        </Span>
        <div>
          <Span>Proof of update:</Span>
          <FileThumbnails files={state?.files} />
        </div>
      </div>
    </PropertyUpdateLayout>
  );
};
